/**
 * Formats elapsed time from a timestamp into a human-readable string
 * 
 * @param {string} timestamp - ISO timestamp to calculate elapsed time from
 * @returns {string} Formatted time string
 * 
 * @example
 * formatTimeAgo('2024-01-01T12:00:00Z') // "00:45 secs ago" (if 45 seconds)
 * formatTimeAgo('2024-01-01T12:00:00Z') // "02:30 min ago" (if 2 mins 30 secs)
 * formatTimeAgo('2024-01-01T12:00:00Z') // "3 hours ago" (if 3 hours)
 */
export const formatTimeAgo = (timestamp: string): string => {
  if (!timestamp) return '0:00 secs ago';
  
  const elapsed = new Date().getTime() - new Date(timestamp).getTime();
  const totalSeconds = Math.floor(elapsed / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  
  // Hours (60+ minutes)
  // FIX: Always return minutes if less than 24 hours, otherwise let it flow (or handle days if needed, though not requested)
  // Actually the request was "Always use minutes" for "Expected 3 hours ago".
  // So we'll convert hours to total minutes display.
  
  if (totalHours > 0) {
    // If it's effectively hours, we still want to show minutes total, e.g. "180 mins ago"
    // However, the original code had nice "00:00" formatting for minutes.
    // Let's stick to the "XX mins ago" or "XX:YY mins ago" format for consistency if that's what is desired,
    // OR simply total minutes integer. 
    // The user example "Took 150 mins" implies integer minutes.
    // The user example "Expected 3 hours ago" -> "FIX: Always use minutes" implies "Expected 180 mins ago".
    
    // We will use the same format logic as the minute block, but without capping at 60.
    const minutesStr = totalMinutes.toString();
    const seconds = totalSeconds % 60;
    // Optional: include seconds or just integer minutes? 
    // The existing minute logic does "02:30 min ago". 
    // If we have 150 minutes, "150:00 mins ago" might be verbose?
    // Let's stick to the existing style but extend it to hours.
    
    const secondsStr = seconds.toString().padStart(2, '0');
    return `${minutesStr}:${secondsStr} mins ago`;
  }
  
  // Minutes (60+ seconds)
  if (totalMinutes > 0) {
    const seconds = totalSeconds % 60;
    const minutesStr = totalMinutes.toString().padStart(2, '0');
    const secondsStr = seconds.toString().padStart(2, '0');
    return totalMinutes === 1 ? `${minutesStr}:${secondsStr} min ago` : `${minutesStr}:${secondsStr} mins ago`;
  }
  
  // Seconds (< 60 seconds)
  const secondsStr = totalSeconds.toString().padStart(2, '0');
  return `00:${secondsStr} secs ago`;
};

/**
 * Generic retry function to be used with any async function.
 *
 * Executes an asynchronous function with exponential backoff retry logic.
 *
 * @template T The type of the value that the Promise resolves to
 *
 * @param {() => Promise<T>} fn - The asynchronous function to execute. Should return a Promise.
 * @param {number} [retries=5] - Maximum number of retry attempts. Defaults to 5.
 *
 * @returns {Promise<T>} A Promise that resolves with the result of the function execution
 *
 * @throws {Error} Throws the last encountered error after all retry attempts fail
 *
 * @example
 * // Simple usage with a fetch request
 * const getData = async () => {
 *   const result = await withRetry(
 *     () => fetch('https://api.example.com/data')
 *   );
 *   return result.json();
 * };
 *
 * // Usage with custom retry count
 * const getData = async () => {
 *   const result = await withRetry(
 *     () => fetch('https://api.example.com/data'),
 *     3  // Only retry 3 times
 *   );
 *   return result.json();
 * };
 *
 * @description
 * The function implements an exponential backoff strategy where each retry attempt
 * waits longer than the previous one. The wait time is calculated as: 2^attempt * 1000ms
 *
 * Wait times for default 5 retries:
 * - 1st retry: 1 second
 * - 2nd retry: 2 seconds
 * - 3rd retry: 4 seconds
 * - 4th retry: 8 seconds
 * - 5th retry: 16 seconds
 */
export const withRetry = async <T,>(
    fn: () => Promise<T>,
    retries = 5
  ): Promise<T> => {
    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (error) {
        if (i === retries - 1) throw error;
        // Wait for (2^i * 1000) milliseconds before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000)
        );
      }
    }
    throw new Error('Retry failed');
  };